import "./GanttLegend.css";

import { JSX } from "react/jsx-runtime";

import { GanttLegendProps, GanttLegendRoadmapProps, LegendItemProps } from "../../models";

const LegendItem = ({ color, borderColor, shape = "bar", label }: LegendItemProps): JSX.Element => (
  <span className="GanttLegendItem">
    {shape === "bar" && (
      <span
        className="GanttLegendBar"
        style={{
          backgroundColor: color,
          borderColor: borderColor ?? color,
        }}
      />
    )}
    {shape === "diamond" && (
      <span className="GanttLegendDiamond" style={{ backgroundColor: color, borderColor: borderColor ?? color }} />
    )}
    {shape === "line" && <span className="GanttLegendLine" style={{ backgroundColor: color }} />}
    {shape === "dashed-line" && <span className="GanttLegendDashedLine" style={{ borderColor: color }} />}
    {shape === "dot" && <span className="GanttLegendDot" style={{ backgroundColor: color }} />}
    <span className="body4 GanttLegendLabel">{label}</span>
  </span>
);

const Divider = (): JSX.Element => <span className="GanttLegendDivider" aria-hidden="true" />;

export const GanttLegend = (props: GanttLegendProps): JSX.Element => {
  const { variant } = props;

  if (variant === "project-roadmap") {
    const { mode = "phase", stages = [] } = props as GanttLegendRoadmapProps;

    if (mode === "stage") {
      return (
        <div className="GanttLegend" role="note" aria-label="Chart legend">
          <span className="GanttLegendGroup">
            <span className="body4 GanttLegendGroupLabel">Stage</span>
            {stages.map((stage) => (
              <LegendItem
                key={stage.label}
                color={stage.fill}
                borderColor={stage.border}
                shape="bar"
                label={stage.label}
              />
            ))}
          </span>
          <Divider />
          <LegendItem color="var(--color-indigo-400)" shape="line" label="Today" />
          <LegendItem color="var(--color-grey-400)" shape="diamond" label="Milestone" />
          <LegendItem color="var(--color-grey-500)" shape="line" label="Key date" />
        </div>
      );
    }

    return (
      <div className="GanttLegend" role="note" aria-label="Chart legend">
        <span className="GanttLegendGroup">
          <span className="body4 GanttLegendGroupLabel">Phase</span>
          <LegendItem
            color="var(--color-indigo-50)"
            borderColor="var(--color-indigo-500)"
            shape="bar"
            label="Feasibility"
          />
          <LegendItem color="var(--color-coral-50)" borderColor="var(--color-coral-500)" shape="bar" label="Design" />
          <LegendItem
            color="var(--color-aqua-50)"
            borderColor="var(--color-aqua-500)"
            shape="bar"
            label="Implementation"
          />
          <LegendItem
            color="var(--color-green-50)"
            borderColor="var(--color-green-500)"
            shape="bar"
            label="Ongoing Monitoring"
          />
        </span>
        <Divider />
        <LegendItem color="var(--color-indigo-400)" shape="line" label="Today" />
        <LegendItem color="var(--color-grey-400)" shape="diamond" label="Milestone" />
        <LegendItem color="var(--color-grey-500)" shape="line" label="Key date" />
      </div>
    );
  }

  return (
    <div className="GanttLegend" role="note" aria-label="Chart legend">
      <span className="GanttLegendGroup">
        <span className="body4 GanttLegendGroupLabel">Status</span>
        <LegendItem color="var(--color-green-500)" shape="dot" label="Completed" />
        <LegendItem color="var(--color-blue-500)" shape="dot" label="In progress" />
        <LegendItem color="var(--color-amber-orange-500)" shape="dot" label="In review" />
        <LegendItem color="var(--color-red-500)" shape="dot" label="Blocked" />
        <LegendItem color="var(--color-grey-400)" shape="dot" label="Not started" />
        <LegendItem color="var(--color-grey-300)" shape="dot" label="Unassigned" />
      </span>
      <Divider />
      <span className="GanttLegendGroup">
        <span className="body4 GanttLegendGroupLabel">Deadline</span>
        <LegendItem color="var(--color-red-50)" borderColor="var(--color-red-200)" shape="bar" label="≤ 1 week" />
        <LegendItem
          color="var(--color-amber-orange-25)"
          borderColor="var(--color-amber-orange-200)"
          shape="bar"
          label="1 week – 1 month"
        />
        <LegendItem
          color="var(--color-mint-green-100)"
          borderColor="var(--color-mint-green-300)"
          shape="bar"
          label="> 1 month"
        />
      </span>
      <Divider />
      <LegendItem color="var(--color-blue-400)" shape="dashed-line" label="Today" />
      <LegendItem color="var(--color-grey-25)" borderColor="var(--color-grey-200)" shape="diamond" label="Milestone" />
    </div>
  );
};
